"use client"

import * as React from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { PARTICIPANT_PROFILE_ENTITY_ID } from '../../../lib/participantProfile'
import {
  buildParticipantCreatePayload,
  createParticipantFormFields,
  createParticipantFormGroups,
  createParticipantFormSchema,
  createParticipantInitialValues,
  type ParticipantFormValues,
} from '../../../components/formConfig'

export default function CreateParticipantPage() {
  const t = useT()
  const router = useRouter()
  const searchParams = useSearchParams()
  const preselectedCustomerEntityId = React.useMemo(() => {
    const value = searchParams?.get('customerEntityId')
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null
  }, [searchParams])

  const formSchema = React.useMemo(() => createParticipantFormSchema(), [])
  const fields = React.useMemo(() => createParticipantFormFields(t), [t])
  const groups = React.useMemo(() => createParticipantFormGroups(t), [t])
  const initialValues = React.useMemo(
    () => createParticipantInitialValues(preselectedCustomerEntityId),
    [preselectedCustomerEntityId],
  )

  return (
    <Page>
      <PageBody>
        <CrudForm<ParticipantFormValues>
          key={preselectedCustomerEntityId ?? 'new'}
          title={t('participants.create.title', 'Create participant')}
          backHref="/backend/participants"
          cancelHref="/backend/participants"
          submitLabel={t('participants.form.submitCreate', 'Create participant')}
          schema={formSchema}
          fields={fields}
          groups={groups}
          entityId={PARTICIPANT_PROFILE_ENTITY_ID}
          initialValues={initialValues}
          onSubmit={async (values) => {
            const payload = buildParticipantCreatePayload(values)
            const response = await createCrud<{ id?: string; participantId?: string }>('participants', payload)
            const createdId =
              typeof response.result?.participantId === 'string'
                ? response.result.participantId
                : (typeof response.result?.id === 'string' ? response.result.id : null)
            flash(t('participants.form.successCreate', 'Participant profile created.'), 'success')
            if (createdId) {
              router.push(`/backend/participants/${createdId}`)
              return
            }
            router.push('/backend/participants')
          }}
        />
      </PageBody>
    </Page>
  )
}
